/**
 * EmployeeConnectionHub — the per-employee Connection Hub (DIGEMP-CONNECTION-HUB-FRONTEND-051).
 *
 * The operator-readable answer, before any session or run, to: which systems is this Digital
 * Employee connected to, what is each connection bound to (customer + target), is it ready and
 * when was it last verified, what are its declared limits, and what does operating it require
 * from a human approver.
 *
 * Read-only projection: every fact comes from records the page already loaded — the agent
 * detail's connection rows, the workspace's entitled tools, the connector registry metadata
 * endpoint (the same manager-gated ["agentConnectorTypes"] query ConnectionsSection uses, so no
 * additional request fires when both are mounted) and, when the backend Connection Hub
 * projection (DIGEMP-CONNECTION-HUB-BACKEND-051) is present on the workspace payload or the
 * agent detail, its per-system entries. Nothing here tests, runs, creates, deletes or approves
 * anything; secret_ref and raw runtime payloads are never rendered.
 *
 * Missing evidence is stated, never guessed: a connection that was never tested reads
 * "טרם נבדק", an unmapped vendor tenant reads "לא ממופה", and connector metadata that could not
 * be loaded (e.g. refused for the current role) degrades to an explicit note instead of a
 * fabricated limitation.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { agentConnectionsApi, companiesApi } from "../services/api";
import { parseUtc } from "../utils/utcDate";

// ─── Static labels ────────────────────────────────────────────────────────────
//
// Fallback only: connector-types (the registry) is authoritative whenever it loads. These cover
// the pre-load render and the role-refused case so a system is never shown as a raw enum token.

const SYSTEM_LABELS = {
  m365_graph: "Microsoft Graph (M365)",
  eset_protect: "ESET Protect",
  acronis_backup: "Acronis Backup",
  openclaw: "OpenClaw",
  teamviewer: "TeamViewer",
  ms365: "Microsoft 365",
  sharepoint: "SharePoint",
  graph: "Microsoft Graph",
  custom: "מותאם אישית",
};

const READINESS_META = {
  ok:            { icon: "✓", label: "תקין",      cls: "bg-green-100 text-green-700" },
  error:         { icon: "✕", label: "שגיאה",     cls: "bg-red-100 text-red-700" },
  untested:      { icon: "○", label: "טרם נבדק",  cls: "bg-gray-100 text-gray-500" },
  unimplemented: { icon: "◌", label: "טרם מומש",  cls: "bg-amber-50 text-amber-700 border border-amber-200" },
  unknown:       { icon: "?", label: "לא ידוע",   cls: "bg-gray-100 text-gray-500" },
};

// Tokens a hub projection entry may use for readiness, normalized to the local states above.
const HUB_READINESS_TOKENS = {
  ok: "ok", healthy: "ok", ready: "ok", verified: "ok",
  error: "error", failed: "error", unavailable: "error",
  untested: "untested", never: "untested", never_tested: "untested",
  unimplemented: "unimplemented", not_implemented: "unimplemented",
  unknown: "unknown",
};

// Tool → system attribution is done only where the Tool Registry's own naming proves it:
// `eset_`-prefixed tools are the ESET connector family. No other association is inferable from
// this screen (the workspace carries tool names + approval flags, not descriptions), so nothing
// else is attributed onto a system card.
const TOOL_SYSTEM_PREFIX = { eset_protect: "eset_" };

// ─── Small helpers ────────────────────────────────────────────────────────────

function strOrNull(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function boundedValue(value, max = 26) {
  const text = strOrNull(value);
  if (!text) return null;
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function shortId(value) {
  const text = value == null ? "" : String(value);
  return text.length > 8 ? `${text.slice(0, 8)}…` : text;
}

function timeAgo(iso) {
  if (!iso) return null;
  const date = parseUtc(iso);
  if (Number.isNaN(date.getTime())) return null;
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return "עכשיו";
  if (minutes < 60) return `לפני ${minutes} דקות`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `לפני ${hours} שעות`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `לפני ${days} ימים`;
  return `לפני ${Math.floor(days / 30)} חודשים`;
}

function fmtAbsolute(iso) {
  const date = parseUtc(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("he-IL", { dateStyle: "short", timeStyle: "short" }).format(date);
}

// ─── Backend hub projection reader (tolerant) ─────────────────────────────────
//
// DIGEMP-CONNECTION-HUB-BACKEND-051 (sibling task, additive): when the projection is present we
// read its per-system entries defensively — any shape we don't recognize is ignored, and every
// consumed field falls back to the locally derived value. Unmatched entries still render (as
// hub-sourced cards) because they carry systems the backend tracks.

function hubGroupsFrom(workspace, agentDetail) {
  const hub = workspace?.connection_hub ?? workspace?.agent?.connection_hub ?? agentDetail?.connection_hub ?? null;
  if (!hub) return [];
  const groups = Array.isArray(hub.groups) ? hub.groups
    : Array.isArray(hub.systems) ? hub.systems
    : Array.isArray(hub) ? hub
    : [];
  return groups.filter((entry) => entry && typeof entry === "object");
}

function entryIdOf(entry) {
  return strOrNull(entry?.connection_id) || strOrNull(entry?.id);
}

function entrySystemType(entry) {
  return strOrNull(entry?.system_type) || strOrNull(entry?.connector_id);
}

function entryReadiness(entry) {
  const raw = strOrNull(entry?.readiness) || strOrNull(entry?.readiness_state) || strOrNull(entry?.readiness_status);
  if (!raw) return null;
  return HUB_READINESS_TOKENS[raw.toLowerCase()] || null;
}

function entryReadinessLabel(entry) {
  return strOrNull(entry?.readiness_label) || strOrNull(entry?.readiness_note);
}

function entryLastVerified(entry) {
  return strOrNull(entry?.last_verified_at) || strOrNull(entry?.last_verified) || strOrNull(entry?.last_tested_at);
}

function entryLimitations(entry) {
  const raw = entry?.limitations;
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => strOrNull(item)).filter(Boolean).slice(0, 6);
}

function entryApprovalText(entry) {
  const raw = entry?.required_approval ?? entry?.approval_requirement ?? entry?.required_approvals;
  if (typeof raw === "string") return strOrNull(raw);
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return strOrNull(raw.label) || strOrNull(raw.level) || strOrNull(raw.summary);
  }
  if (Array.isArray(raw)) {
    const parts = raw.map((item) => typeof item === "string" ? strOrNull(item) : strOrNull(item?.label)).filter(Boolean);
    return parts.length ? parts.slice(0, 4).join(" · ") : null;
  }
  if (entry?.strong_approval_required === true) return "נדרש אישור מוגבר (Tier 3)";
  if (entry?.approval_required === true) return "נדרש אישור טכנאי";
  return null;
}

// ─── Card model ───────────────────────────────────────────────────────────────

function readinessFromRecord(connection, defn) {
  const status = strOrNull(connection?.last_test_status);
  if (status === "ok" || status === "success") return "ok";
  if (status === "error" || status === "failed") return "error";
  // Recorded status always wins: a live connector test can pass even while its declared
  // capabilities remain unimplemented (see Acronis). Only a never-tested connector whose
  // definition says the connector is unimplemented gets the more informative state.
  if (!status) return defn?.implemented === false ? "unimplemented" : "untested";
  return "unknown";
}

function attributedTools(tools, systemType) {
  const prefix = TOOL_SYSTEM_PREFIX[systemType];
  if (!prefix) return [];
  return tools.filter((tool) => strOrNull(tool?.name)?.startsWith(prefix));
}

function derivedCard(connection, defn, entry, ctx) {
  const { agentDetail, companiesById, tools, bindingAware } = ctx;
  const systemType = connection.system_type;
  const systemLabel = defn?.display_name || SYSTEM_LABELS[systemType] || systemType;

  const hubToken = entryReadiness(entry);
  const derived = readinessFromRecord(connection, defn);
  const readiness = hubToken || derived;

  const fieldLabel = (name, fallback) => defn?.fields?.find((f) => f.name === name)?.label || fallback;
  const declares = (name) => !!defn?.fields?.some((f) => f.name === name);

  const limitations = [];
  if (defn?.read_only === true) limitations.push("קריאה בלבד");
  if (defn?.implemented === false) limitations.push("טרם מומש — יכולות מוצהרות טרם הוטמעו");
  if (defn?.supports_test === false) limitations.push("אין בדיקת חיבור ייעודית");
  if (defn?.target_selection === "device") limitations.push("נדרשת בחירת מכשיר יעד לפני הפעלה");
  if (declares("partner_tenant_id") && !strOrNull(connection.partner_tenant_id)) {
    limitations.push("אין Tenant שותף — גילוי Tenants ייכשל");
  }
  if (declares("external_tenant_id") && !strOrNull(connection.external_tenant_id)) {
    limitations.push("לא ממופה ל-Tenant חיצוני — פעולות ייכשלו עד מיפוי");
  }
  for (const item of entryLimitations(entry)) limitations.push(item);

  const approvals = [];
  if (defn?.read_only === true) approvals.push("קריאה בלבד — אין נתיב פעולת שינוי דרך מערכת זו");
  if (defn?.read_only === false) approvals.push("פעולות שינוי דרך מערכת זו מחייבות אישור טכנאי לכל פעולה");
  const hubApproval = entryApprovalText(entry);
  if (hubApproval) approvals.push(hubApproval);

  const targetChips = [];
  const tenant = boundedValue(connection.tenant_id);
  if (tenant) {
    targetChips.push({ kind: "value", label: fieldLabel("tenant_id", "מזהה ארגון (Tenant)"), value: tenant, title: connection.tenant_id });
  }
  if (declares("partner_tenant_id") && strOrNull(connection.partner_tenant_id)) {
    targetChips.push({ kind: "value", label: fieldLabel("partner_tenant_id", "מזהה Tenant שותף"), value: boundedValue(connection.partner_tenant_id), title: connection.partner_tenant_id });
  }
  if (declares("external_tenant_id") && strOrNull(connection.external_tenant_id)) {
    targetChips.push({ kind: "value", label: "ממופה ל-Tenant חיצוני", value: boundedValue(connection.external_tenant_id), title: connection.external_tenant_id });
  }

  let customerText = null;
  if (bindingAware) {
    if (strOrNull(connection.customer_id)) {
      const name = companiesById[String(connection.customer_id)];
      customerText = name ? `מוגבל ללקוח: ${name}` : `מוגבל ללקוח — מזהה ${shortId(connection.customer_id)}`;
    } else if (agentDetail?.client_id) {
      customerText = agentDetail.client_name
        ? `לקוח העובד: ${agentDetail.client_name}`
        : `לקוח העובד — מזהה ${shortId(agentDetail.client_id)}`;
    } else {
      customerText = "ללא שיוך ללקוח (כלל-ארגוני)";
    }
  }

  return {
    key: `conn-${connection.id ?? systemType}`,
    source: entry ? "hub" : "derived",
    systemLabel,
    displayName: strOrNull(connection.display_name),
    readiness,
    readinessNote: entryReadinessLabel(entry),
    lastVerifiedAt: entryLastVerified(entry) || strOrNull(connection.last_tested_at),
    customerText,
    targetChips,
    limitations,
    limitationsEmptyText: defn ? "אין מגבלות מוצהרות" : "פרטי המחבר טרם נטענו",
    approvals,
    attributed: attributedTools(tools, systemType),
    bindingAware,
  };
}

function hubOnlyCard(entry, index) {
  const targetValue = strOrNull(entry.target) || strOrNull(entry.target_id);
  const customerText = strOrNull(entry.customer_name) || strOrNull(entry.customer) || "לא ידוע";
  const approval = entryApprovalText(entry);
  return {
    key: `hub-${entryIdOf(entry) ?? entrySystemType(entry) ?? index}`,
    source: "hub",
    systemLabel: strOrNull(entry.provider_name) || strOrNull(entry.display_name) || entrySystemType(entry) || "מערכת לא מזוהה",
    displayName: null,
    readiness: entryReadiness(entry) || "unknown",
    readinessNote: entryReadinessLabel(entry),
    lastVerifiedAt: entryLastVerified(entry),
    customerText,
    targetChips: targetValue
      ? [{ kind: "value", label: "יעד", value: boundedValue(targetValue), title: targetValue }]
      : [{ kind: "muted", label: "לא ידוע" }],
    limitations: entryLimitations(entry),
    limitationsEmptyText: "לא צוינו מגבלות בפרויקציה",
    approvals: approval ? [approval] : [],
    attributed: [],
    bindingAware: true,
  };
}

function buildCards(connections, connectorById, ctx) {
  const { hubGroups } = ctx;
  const usedHub = new Set();
  const cards = [];

  for (const connection of connections) {
    let entry = null;
    if (hubGroups.length) {
      let index = hubGroups.findIndex((candidate, i) => !usedHub.has(i) && entryIdOf(candidate) && String(entryIdOf(candidate)) === String(connection.id));
      if (index === -1) {
        index = hubGroups.findIndex((candidate, i) => !usedHub.has(i) && entrySystemType(candidate) && entrySystemType(candidate) === connection.system_type);
      }
      if (index !== -1) {
        entry = hubGroups[index];
        usedHub.add(index);
      }
    }
    cards.push(derivedCard(connection, connectorById[connection.system_type] || null, entry, ctx));
  }

  hubGroups.forEach((entry, index) => {
    if (!usedHub.has(index)) cards.push(hubOnlyCard(entry, index));
  });

  return cards;
}

// ─── Presentational pieces ────────────────────────────────────────────────────

function ReadinessBadge({ readiness, note }) {
  const meta = READINESS_META[readiness] || READINESS_META.unknown;
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${meta.cls}`}>
      {meta.icon} {note || meta.label}
    </span>
  );
}

function HubField({ label, children }) {
  if (children == null) return null;
  return (
    <div className="min-w-0">
      <div className="text-[11px] text-gray-400">{label}</div>
      <div className="text-xs text-gray-700">{children}</div>
    </div>
  );
}

function HubCard({ card }) {
  const verifiedAgo = timeAgo(card.lastVerifiedAt);
  return (
    <article data-testid={`connection-hub-card-${card.key}`} className="rounded-lg border border-gray-100 bg-gray-50/60 p-3 space-y-2">
      <header className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-800">{card.systemLabel}</span>
        {card.displayName && <span className="text-xs text-gray-400">{card.displayName}</span>}
        <ReadinessBadge readiness={card.readiness} note={card.readinessNote} />
        {card.source === "hub" && (
          <span className="text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-1.5 py-0.5">
            מפרויקציית החיבורים
          </span>
        )}
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2">
        <HubField label="לקוח">
          {card.customerText
            ? <span>{card.customerText}</span>
            : <span className="text-gray-400">לא נטען בתצוגה זו</span>}
        </HubField>
        <HubField label="יעד">
          <div className="flex flex-wrap items-center gap-1">
            {card.targetChips.length === 0 && <span className="text-gray-400">לא צוין יעד</span>}
            {card.targetChips.map((chip, i) => (
              chip.kind === "value" ? (
                <span key={i} className="text-[10px] text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-1.5 py-0.5">
                  {chip.label}: <span className="font-mono" dir="ltr" title={chip.title}>{chip.value}</span>
                </span>
              ) : (
                <span key={i} className={`text-[10px] rounded-full px-1.5 py-0.5 ${chip.kind === "attention" ? "text-amber-700 bg-amber-50 border border-amber-200" : "text-gray-400 bg-gray-100"}`}>
                  {chip.label}
                </span>
              )
            ))}
          </div>
        </HubField>
        <HubField label="אומת לאחרונה">
          {verifiedAgo ? (
            <span title={fmtAbsolute(card.lastVerifiedAt)}>{verifiedAgo} <span className="text-gray-400">({fmtAbsolute(card.lastVerifiedAt)})</span></span>
          ) : (
            <span className="text-gray-400">טרם בוצעה בדיקת חיבור</span>
          )}
        </HubField>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] text-gray-400">מגבלות:</span>
        {card.limitations.length === 0 && (
          <span className="text-[11px] text-gray-400">{card.limitationsEmptyText}</span>
        )}
        {card.limitations.map((item, i) => (
          <span key={i} className="text-[10px] text-gray-600 bg-gray-100 border border-gray-200 rounded-full px-1.5 py-0.5">{item}</span>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] text-gray-400">אישורים:</span>
        {card.approvals.length > 0 && <span className="text-[11px] text-gray-700">{card.approvals.join(" · ")}</span>}
        {card.approvals.length === 0 && card.attributed.length === 0 && (
          <span className="text-[11px] text-gray-400">אין מידע אישורים לתצוגה זו</span>
        )}
        {card.attributed.length > 0 && (
          <>
            <span className="text-[11px] text-gray-500">
              {card.attributed.length} כלים משויכים
            </span>
            {card.attributed.map((tool) => (
              <span
                key={tool.name}
                title={tool.strong_approval_required ? "דורש אישור מוגבר (Tier 3)" : tool.approval_required ? "דורש אישור טכנאי" : "קריאה בלבד — ללא אישור"}
                className={`text-[10px] px-1.5 py-0.5 rounded border font-mono ${
                  tool.strong_approval_required ? "bg-purple-50 border-purple-200 text-purple-700"
                  : tool.approval_required ? "bg-orange-50 border-orange-200 text-orange-700"
                  : "bg-blue-50 border-blue-200 text-blue-700"
                }`}
              >
                {tool.name}
              </span>
            ))}
          </>
        )}
      </div>
    </article>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function EmployeeConnectionHub({ agentDetail = null, workspace = null }) {
  // Same query key as ConnectionsSection: when both are mounted one shared request serves both,
  // and an operator without connector-metadata rights gets the same (refused) outcome the
  // existing connections surface already produces — degraded below with an explicit note.
  const { data: connectorTypes, isError: connectorTypesError } = useQuery({
    queryKey: ["agentConnectorTypes"],
    queryFn: () => agentConnectionsApi.connectorTypes(),
    staleTime: 5 * 60 * 1000,
  });

  const fullConnections = Array.isArray(agentDetail?.connections) ? agentDetail.connections : null;
  const connections = useMemo(
    () => fullConnections
      ?? (Array.isArray(workspace?.capabilities?.connections) ? workspace.capabilities.connections : []),
    [fullConnections, workspace],
  );
  const bindingAware = fullConnections !== null;

  const needsCompanies = connections.some((connection) => strOrNull(connection?.customer_id));
  const { data: companies } = useQuery({
    queryKey: ["companies"],
    queryFn: () => companiesApi.list(),
    enabled: needsCompanies,
    staleTime: 5 * 60 * 1000,
  });

  const tools = useMemo(
    () => (Array.isArray(workspace?.capabilities?.tools) ? workspace.capabilities.tools : []),
    [workspace],
  );

  const connectorById = useMemo(() => {
    const byId = {};
    for (const defn of Array.isArray(connectorTypes) ? connectorTypes : []) {
      if (defn?.connector_id) byId[defn.connector_id] = defn;
    }
    return byId;
  }, [connectorTypes]);

  const companiesById = useMemo(() => {
    const byId = {};
    for (const company of Array.isArray(companies) ? companies : []) {
      if (company?.id != null) byId[String(company.id)] = strOrNull(company.name) || String(company.id);
    }
    return byId;
  }, [companies]);

  const hubGroups = useMemo(() => hubGroupsFrom(workspace, agentDetail), [workspace, agentDetail]);

  const cards = useMemo(
    () => buildCards(connections, connectorById, { agentDetail, companiesById, tools, hubGroups, bindingAware }),
    [connections, connectorById, agentDetail, companiesById, tools, hubGroups, bindingAware],
  );

  const summary = cards.reduce((acc, card) => {
    acc[card.readiness] = (acc[card.readiness] || 0) + 1;
    return acc;
  }, {});
  const systemCount = new Set(cards.map((card) => card.systemLabel)).size;
  const approvalTools = tools.filter((tool) => tool?.approval_required || tool?.strong_approval_required);

  return (
    <section
      dir="rtl"
      aria-label="מרכז החיבורים"
      data-testid="employee-connection-hub"
      className="bg-white rounded-xl border shadow-sm p-4 space-y-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-700">מרכז החיבורים</h3>
        <span className="text-xs text-gray-400">
          {systemCount} מערכות · {cards.length} חיבורים
        </span>
      </div>
      <p className="text-[11px] text-gray-400">
        מערכות העובד, שיוך ללקוח וליעד, מוכנות ואימות אחרון, מגבלות ואישורי פעולה — לקריאה בלבד,
        מבוסס על רשומות החיבורים והבדיקות הקיימות.
      </p>

      {cards.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {summary.ok > 0 && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">מאומתות: {summary.ok}</span>
          )}
          {summary.error > 0 && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">שגיאה: {summary.error}</span>
          )}
          {summary.untested > 0 && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">טרם נבדקו: {summary.untested}</span>
          )}
          {summary.unimplemented > 0 && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">טרם מומשו: {summary.unimplemented}</span>
          )}
          {summary.unknown > 0 && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">לא ידוע: {summary.unknown}</span>
          )}
          {tools.length > 0 && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200 font-medium">
              כלים דורשי אישור: {approvalTools.length} מתוך {tools.length}
            </span>
          )}
        </div>
      )}

      {connectorTypesError && cards.length > 0 && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          פרטי המחברים (מגבלות וסוגי בדיקה) לא נטענו בהרשאה זו — הכרטיסים מוצגים חלקית, בלי להמציא מגבלות.
        </p>
      )}

      {cards.length === 0 ? (
        <p className="text-sm text-gray-400 italic">לעובד זה לא הוגדרו חיבורי מערכות</p>
      ) : (
        <div className="space-y-2.5">
          {cards.map((card) => <HubCard key={card.key} card={card} />)}
        </div>
      )}
    </section>
  );
}
